import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@finitless/shared/hooks";
import { useUserStore } from "@finitless/shared/stores";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { DeleteConfirmationModal } from "../../ui/Modals";
import { Breadcrumb, ContextHelp, Icon, Input, Label, Switch } from "../../ui";
import MetaAdsBusinessPicker from "./MetaAdsBusinessPicker";
import MetaAdsCreativePreview from "./MetaAdsCreativePreview";
import MetaAdsEditCampaignSkeleton from "./MetaAdsEditCampaignSkeleton";
import {
  isMetaAdsApiConfigured,
  launchMetaCampaign,
  patchMetaAdsCampaignBundle,
} from "../../../hooks/metaAds/useMetaAdsApi";
import {
  broadcastMetaAdsConnectionChanged,
  META_ADS_CONNECTION_STATUS_QUERY_KEY,
} from "../../../hooks/metaAds/useMetaAdsConnectionStatus";
import { useMetaAdsCampaignForEdit } from "../../../hooks/metaAds/useMetaAdsCampaignForEdit";
import { useMetaAdsDeleteCampaign } from "../../../hooks/metaAds/useMetaAdsDeleteCampaign";
import { isMetaAdsExperienceAvailable } from "../../../hooks/metaAds/useMetaAdsMode";
import { cn } from "@/lib/utils";
import {
  metaAdsDangerZoneDeleteButtonClass,
  metaAdsFormFieldLabelClass,
  metaAdsFormSectionClass,
  metaAdsFormSectionLabelClass,
  metaAdsGradientCtaClass,
  metaAdsOutlineCtaClass,
  metaAdsTextareaClass,
  metaAdsTitleGradientClass,
} from "./metaAdsTheme";
import {
  META_ADS_CAMPAIGN_OBJECTIVE_SALES,
  META_ADS_CAMPAIGN_OBJECTIVE_TRAFFIC,
} from "./metaAdsUtils";

function getEnvFacebookPageId() {
  return String(import.meta.env.VITE_META_ADS_FACEBOOK_PAGE_ID || "").trim();
}

function getEnvMetaPixelId() {
  return String(import.meta.env.VITE_META_ADS_PIXEL_ID || "").trim();
}

function maskNumericIdForDisplay(value) {
  if (!value) {
    return "";
  }
  return "•".repeat(value.length);
}

function getPreviewAccountLabel(business, user) {
  const name = business?.name?.trim();
  if (name) {
    return name;
  }
  if (!user || typeof user !== "object") {
    return "";
  }
  const fromParts = [user.firstname, user.lastname]
    .map((p) => (p != null ? String(p).trim() : ""))
    .filter(Boolean);
  if (fromParts.length) {
    return fromParts.join(" ");
  }
  if (user.name && String(user.name).trim()) {
    return String(user.name).trim();
  }
  if (user.email && String(user.email).trim()) {
    const local = String(user.email).split("@")[0];
    return local || "";
  }
  return "";
}

function getPreviewContactEmail(business, user) {
  const be = business?.email;
  if (be != null && String(be).trim()) {
    return String(be).trim();
  }
  if (user?.email && String(user.email).trim()) {
    return String(user.email).trim();
  }
  return "";
}

const MetaAdsCreateCampaignPage = () => {
  const { campaignId: routeCampaignId } = useParams();
  const { t } = useTranslation("metaAds");
  const { t: tNav } = useTranslation("navigation");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const apiConfigured = isMetaAdsApiConfigured();
  const unlocked = isMetaAdsExperienceAvailable(apiConfigured);
  const isEditMode = Boolean(String(routeCampaignId ?? "").trim());
  const {
    data: campaignDetail,
    isLoading: isCampaignDetailLoading,
    isError: isCampaignDetailError,
    error: campaignDetailError,
  } = useMetaAdsCampaignForEdit(routeCampaignId, {
    enabled: Boolean(unlocked && isEditMode),
  });

  const user = useUserStore((s) => s.user);

  /** Local pick for this flow only (not global business store). */
  const [campaignBusinessId, setCampaignBusinessId] = useState("");
  /** Full row from {@link MetaAdsBusinessPicker} so previews work when the list is paginated. */
  const [selectedBusinessRow, setSelectedBusinessRow] = useState(null);
  const [businessAvailability, setBusinessAvailability] = useState({
    total: null,
    loading: true,
  });

  const handleBusinessesMeta = useCallback((meta) => {
    setBusinessAvailability({
      total: meta.total ?? 0,
      loading: Boolean(meta.isLoading),
    });
  }, []);

  const handleSelectBusiness = useCallback((b) => {
    if (b == null) {
      setCampaignBusinessId("");
      setSelectedBusinessRow(null);
      return;
    }
    setCampaignBusinessId(String(b.id));
    setSelectedBusinessRow(b);
  }, []);

  const selectedBusiness = useMemo(() => {
    if (campaignBusinessId === "") {
      return null;
    }
    if (
      selectedBusinessRow != null &&
      String(selectedBusinessRow.id) === String(campaignBusinessId)
    ) {
      return selectedBusinessRow;
    }
    return { id: campaignBusinessId, name: "", email: null, logo: null };
  }, [campaignBusinessId, selectedBusinessRow]);

  const storeId = useMemo(() => {
    if (selectedBusiness?.id == null) {
      return "";
    }
    return String(selectedBusiness.id);
  }, [selectedBusiness?.id]);

  const previewAccountLabel = useMemo(
    () => getPreviewAccountLabel(selectedBusiness, user),
    [selectedBusiness, user],
  );
  const previewContactEmail = useMemo(
    () => getPreviewContactEmail(selectedBusiness, user),
    [selectedBusiness, user],
  );

  const [name, setName] = useState("");
  const [dailyBudgetDollars, setDailyBudgetDollars] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [primaryText, setPrimaryText] = useState("");
  const [pageId, setPageId] = useState("");
  const [campaignObjective, setCampaignObjective] = useState(
    META_ADS_CAMPAIGN_OBJECTIVE_TRAFFIC,
  );
  /** Meta requires this flag for ad set-level budgets; off = full amount on this ad set. */
  const [isAdsetBudgetSharingEnabled, setIsAdsetBudgetSharingEnabled] =
    useState(false);
  /** Env Page ID: hidden by default, toggle with eye control. */
  const [showEnvPageId, setShowEnvPageId] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const envFacebookPageId = useMemo(() => getEnvFacebookPageId(), []);
  const envMetaPixelId = useMemo(() => getEnvMetaPixelId(), []);

  const editHydratedKeyRef = useRef(null);

  useEffect(() => {
    if (!isEditMode) {
      editHydratedKeyRef.current = null;
      setCampaignBusinessId("");
      setSelectedBusinessRow(null);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode || !campaignDetail?.campaignId) {
      return;
    }
    const key = String(campaignDetail.campaignId);
    if (editHydratedKeyRef.current === key) {
      return;
    }
    editHydratedKeyRef.current = key;

    setName(campaignDetail.name || "");
    const cents = Number(campaignDetail.dailyBudgetCents ?? 0);
    const dollars = cents / 100;
    setDailyBudgetDollars(
      Number.isFinite(dollars) && dollars >= 0 ? dollars.toFixed(2) : "",
    );
    setDestinationUrl(
      typeof campaignDetail.destinationUrl === "string"
        ? campaignDetail.destinationUrl
        : "",
    );
    setImageUrl(
      typeof campaignDetail.imageUrl === "string"
        ? campaignDetail.imageUrl
        : "",
    );
    setPrimaryText(
      typeof campaignDetail.primaryText === "string"
        ? campaignDetail.primaryText
        : "",
    );
    if (!envFacebookPageId) {
      const pid =
        typeof campaignDetail.pageId === "string"
          ? campaignDetail.pageId.trim()
          : "";
      if (pid) {
        setPageId(pid);
      }
    }
    const obj = String(campaignDetail.objective || "")
      .trim()
      .toUpperCase();
    if (obj === META_ADS_CAMPAIGN_OBJECTIVE_SALES) {
      setCampaignObjective(META_ADS_CAMPAIGN_OBJECTIVE_SALES);
    } else {
      setCampaignObjective(META_ADS_CAMPAIGN_OBJECTIVE_TRAFFIC);
    }
    setIsAdsetBudgetSharingEnabled(
      Boolean(campaignDetail.isAdsetBudgetSharingEnabled),
    );
    const rawStore = campaignDetail.storeId;
    if (rawStore != null && String(rawStore).trim() !== "") {
      setCampaignBusinessId(String(rawStore).trim());
    }
  }, [isEditMode, campaignDetail, envFacebookPageId]);

  const editStoreIdFromApi = useMemo(() => {
    const s = campaignDetail?.storeId;
    if (s == null || s === "") {
      return null;
    }
    const t = String(s).trim();
    return t !== "" ? t : null;
  }, [campaignDetail?.storeId]);

  const { data: editPrefetchBusiness } = useQuery({
    queryKey: ["meta-ads-edit-business-by-id", editStoreIdFromApi],
    queryFn: async () => {
      const id = Number(editStoreIdFromApi);
      if (!Number.isFinite(id)) {
        return null;
      }
      const whereClause = {
        conditions: [{ attribute: "id", value: id }],
        conector: "AND",
      };
      const queryString = buildQueryParams({
        params: "id,logo,name,enabled,email",
        where: whereClause,
        page: 1,
        page_size: 1,
      });
      const response = await apiClient.get(
        `/backoffice/businesses${queryString ? `?${queryString}` : ""}`,
      );
      const list = response?.result ?? [];
      return Array.isArray(list) && list[0] ? list[0] : null;
    },
    enabled:
      Boolean(unlocked && isEditMode && editStoreIdFromApi) &&
      isEndpointEnabled("business", "list"),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!isEditMode || !editPrefetchBusiness || !editStoreIdFromApi) {
      return;
    }
    if (String(campaignBusinessId) !== String(editStoreIdFromApi)) {
      return;
    }
    setSelectedBusinessRow(editPrefetchBusiness);
  }, [
    isEditMode,
    editPrefetchBusiness,
    editStoreIdFromApi,
    campaignBusinessId,
  ]);

  const resolvedPageId = envFacebookPageId || pageId.trim();
  const hasPageConfigured = Boolean(resolvedPageId);
  const salesNeedsPixelInEnv =
    campaignObjective === META_ADS_CAMPAIGN_OBJECTIVE_SALES && !envMetaPixelId;

  useEffect(() => {
    if (
      !envMetaPixelId &&
      campaignObjective === META_ADS_CAMPAIGN_OBJECTIVE_SALES
    ) {
      setCampaignObjective(META_ADS_CAMPAIGN_OBJECTIVE_TRAFFIC);
    }
  }, [envMetaPixelId, campaignObjective]);

  const hasBusinessContext =
    businessAvailability.loading || (businessAvailability.total ?? 0) > 0;

  const previewImageSrc = imageUrl.trim() || "";

  const breadcrumbItems = useMemo(
    () => [
      { label: tNav("sidebar.growth"), disabled: true },
      {
        label: tNav("sidebar.metaAds"),
        path: "/growth/meta-ads",
        icon: "Share2",
      },
      ...(isEditMode
        ? [
            {
              label: t("create.editBreadcrumbLabel"),
              active: true,
              disabled: true,
            },
          ]
        : [
            {
              label: `${t("create.titleGradientWord")}${t("create.titleRest")}`,
              active: true,
              disabled: true,
            },
          ]),
    ],
    [isEditMode, t, tNav],
  );

  const mutation = useMutation({
    mutationFn: (body) => launchMetaCampaign(body),
    onSuccess: (_data, vars) => {
      showToast(t("create.successToast"), "success");
      queryClient.invalidateQueries({
        queryKey: META_ADS_CONNECTION_STATUS_QUERY_KEY,
      });
      broadcastMetaAdsConnectionChanged();
      navigate("/growth/meta-ads/success", {
        state: {
          campaignName: vars.name,
        },
      });
    },
    onError: (err) => {
      showToast(err?.message || t("create.errorToast"), "error");
    },
  });

  const saveEditMutation = useMutation({
    mutationFn: ({ id, body }) => patchMetaAdsCampaignBundle(id, body),
    onSuccess: (data, variables) => {
      showToast(t("create.editSuccessToast"), "success");
      const cid = String(variables?.id ?? "").trim();
      if (cid) {
        queryClient.setQueryData(["meta-ads-campaign-detail", cid], data);
      }
      queryClient.invalidateQueries({
        queryKey: ["meta-ads-graph-campaigns"],
        exact: false,
      });
    },
    onError: (err) => {
      showToast(err?.message || t("create.editSaveErrorToast"), "error");
    },
  });

  const isSaving = mutation.isPending || saveEditMutation.isPending;

  const deleteCampaignMutation = useMetaAdsDeleteCampaign();

  const handleConfirmDeleteCampaign = useCallback(() => {
    const cid = String(routeCampaignId ?? "").trim();
    if (!cid) {
      setDeleteModalOpen(false);
      return;
    }
    deleteCampaignMutation.mutate(
      { campaignId: cid },
      {
        onSuccess: () => {
          setDeleteModalOpen(false);
          navigate("/growth/meta-ads");
        },
      },
    );
  }, [deleteCampaignMutation, navigate, routeCampaignId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditMode) {
      const cid = String(routeCampaignId ?? "").trim();
      if (!cid) {
        return;
      }
      if (!name.trim()) {
        showToast(t("create.campaignNameRequired"), "error");
        return;
      }
      if (
        campaignObjective === META_ADS_CAMPAIGN_OBJECTIVE_SALES &&
        !envMetaPixelId
      ) {
        showToast(t("create.salesPixelEnvRequired"), "error");
        return;
      }
      if (!resolvedPageId) {
        showToast(t("create.pageIdRequired"), "error");
        return;
      }
      const dollars = Number.parseFloat(
        String(dailyBudgetDollars).replace(",", "."),
      );
      if (!Number.isFinite(dollars) || dollars <= 0) {
        showToast(t("create.invalidBudgetDollars"), "error");
        return;
      }
      const dailyBudgetCents = Math.round(dollars * 100);
      const trimmedImageUrl = imageUrl.trim();
      if (!trimmedImageUrl) {
        showToast(t("create.imageRequired"), "error");
        return;
      }
      const body = {
        name: name.trim(),
        dailyBudgetCents,
        destinationUrl: destinationUrl.trim(),
        imageUrl: trimmedImageUrl,
        primaryText: primaryText.trim(),
        pageId: resolvedPageId,
        pixelId:
          campaignObjective === META_ADS_CAMPAIGN_OBJECTIVE_SALES &&
          envMetaPixelId
            ? envMetaPixelId
            : undefined,
        campaignObjective,
        isAdsetBudgetSharingEnabled,
        ...(storeId ? { storeId } : {}),
      };
      saveEditMutation.mutate({ id: cid, body });
      return;
    }
    if (!name.trim()) {
      showToast(t("create.campaignNameRequired"), "error");
      return;
    }
    if (!hasBusinessContext) {
      showToast(t("create.noBusinesses"), "error");
      return;
    }
    if (!storeId) {
      showToast(t("create.selectBusinessRequired"), "error");
      return;
    }
    if (
      campaignObjective === META_ADS_CAMPAIGN_OBJECTIVE_SALES &&
      !envMetaPixelId
    ) {
      showToast(t("create.salesPixelEnvRequired"), "error");
      return;
    }
    if (!resolvedPageId) {
      showToast(t("create.pageIdRequired"), "error");
      return;
    }
    const dollars = Number.parseFloat(
      String(dailyBudgetDollars).replace(",", "."),
    );
    if (!Number.isFinite(dollars) || dollars <= 0) {
      showToast(t("create.invalidBudgetDollars"), "error");
      return;
    }
    const dailyBudgetCents = Math.round(dollars * 100);

    const trimmedImageUrl = imageUrl.trim();
    if (!trimmedImageUrl) {
      showToast(t("create.imageRequired"), "error");
      return;
    }

    mutation.mutate({
      storeId,
      name: name.trim(),
      dailyBudgetCents,
      destinationUrl: destinationUrl.trim(),
      imageUrl: trimmedImageUrl,
      primaryText: primaryText.trim(),
      pageId: resolvedPageId,
      pixelId:
        campaignObjective === META_ADS_CAMPAIGN_OBJECTIVE_SALES &&
        envMetaPixelId
          ? envMetaPixelId
          : undefined,
      campaignObjective,
      isAdsetBudgetSharingEnabled,
    });
  };

  const hasCreativeImage = Boolean(imageUrl.trim());

  const disableSubmit =
    (isEditMode && (isCampaignDetailLoading || isCampaignDetailError)) ||
    !unlocked ||
    !name.trim() ||
    (!isEditMode && !storeId) ||
    isSaving ||
    !destinationUrl.trim() ||
    !hasPageConfigured ||
    !primaryText.trim() ||
    !hasCreativeImage ||
    salesNeedsPixelInEnv;

  return (
    <div className="space-y-10">
      <Breadcrumb items={breadcrumbItems} />

      {!unlocked && (
        <div
          className={cn(
            "rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100",
          )}
        >
          {t("hub.apiNotConfigured")}
        </div>
      )}

      {!businessAvailability.loading &&
        (businessAvailability.total ?? 0) === 0 &&
        !isEditMode && (
          <div
            className={cn(
              "border-destructive/35 bg-destructive/10 rounded-xl border px-4 py-3 text-sm",
              "text-foreground",
            )}
          >
            {t("create.noBusinesses")}
          </div>
        )}

      {isEditMode && unlocked && isCampaignDetailLoading ? (
        <MetaAdsEditCampaignSkeleton />
      ) : isEditMode && unlocked && isCampaignDetailError ? (
        <div className="border-destructive/35 bg-destructive/10 rounded-xl border px-4 py-6 text-sm text-red-100">
          {campaignDetailError?.message || t("create.editError")}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {isEditMode ? (
                  <>
                    <span className={metaAdsTitleGradientClass}>
                      {t("create.editTitleGradientWord")}
                    </span>
                    <span className="text-foreground">
                      {t("create.editTitleRest")}
                    </span>
                  </>
                ) : (
                  <>
                    <span className={metaAdsTitleGradientClass}>
                      {t("create.titleGradientWord")}
                    </span>
                    <span className="text-foreground">
                      {t("create.titleRest")}
                    </span>
                  </>
                )}
              </h1>
              <p className="text-muted-foreground mt-2 max-w-2xl text-sm sm:text-base">
                {isEditMode ? t("create.editSubtitle") : t("create.subtitle")}
              </p>
            </div>
            <Link
              to="/growth/meta-ads/performance"
              className={cn(
                metaAdsOutlineCtaClass,
                "focus-visible:ring-brand-cyan/35 inline-flex w-full shrink-0 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none sm:w-auto",
              )}
            >
              {t("create.cancelToPerformance")}
            </Link>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid gap-8 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <section className={metaAdsFormSectionClass}>
                  <h2 className={metaAdsFormSectionLabelClass}>
                    {t("create.sectionCampaignName")}
                  </h2>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t("create.campaignNameHint")}
                  </p>
                  <div className="mt-4 space-y-2">
                    <Label
                      htmlFor="meta-campaign-name"
                      className={metaAdsFormFieldLabelClass}
                    >
                      {t("create.campaignName")}
                    </Label>
                    <Input
                      id="meta-campaign-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t("create.campaignNamePlaceholder")}
                      required
                      variant="dark"
                      size="lg"
                    />
                  </div>
                </section>

                <section className={metaAdsFormSectionClass}>
                  <h2 className={metaAdsFormSectionLabelClass}>
                    {t("create.sectionAccount")}
                  </h2>
                  <div className="mt-3">
                    <MetaAdsBusinessPicker
                      className="mt-2"
                      value={campaignBusinessId}
                      onSelect={handleSelectBusiness}
                      onMetaChange={handleBusinessesMeta}
                      pinnedRow={selectedBusinessRow}
                    />
                    {!businessAvailability.loading &&
                      (businessAvailability.total ?? 0) === 0 && (
                        <p className="text-muted-foreground mt-3 text-sm">
                          {t("create.noBusinesses")}
                        </p>
                      )}
                  </div>
                </section>

                <section className={metaAdsFormSectionClass}>
                  <h2 className={metaAdsFormSectionLabelClass}>
                    {t("create.sectionLanding")}
                  </h2>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t("create.sectionLandingHint")}
                  </p>
                  <div className="mt-4 space-y-2">
                    <Label
                      htmlFor="meta-dest"
                      className={metaAdsFormFieldLabelClass}
                    >
                      {t("create.destinationUrl")}
                    </Label>
                    <Input
                      id="meta-dest"
                      type="url"
                      value={destinationUrl}
                      onChange={(e) => setDestinationUrl(e.target.value)}
                      placeholder={t("create.destinationUrlPlaceholder")}
                      required
                      variant="dark"
                      size="lg"
                    />
                  </div>
                </section>

                <section className={metaAdsFormSectionClass}>
                  <h2 className={metaAdsFormSectionLabelClass}>
                    {t("create.sectionObjective")}
                  </h2>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() =>
                        setCampaignObjective(
                          META_ADS_CAMPAIGN_OBJECTIVE_TRAFFIC,
                        )
                      }
                      className={cn(
                        "rounded-xl border p-4 text-left transition",
                        campaignObjective ===
                          META_ADS_CAMPAIGN_OBJECTIVE_TRAFFIC
                          ? "border-brand-cyan/50 bg-brand-cyan/10 ring-brand-cyan/20 ring-1"
                          : "border-border bg-muted/20 hover:border-muted-foreground/30",
                      )}
                    >
                      <p className="text-foreground font-semibold">
                        {t("create.objectiveTileTrafficTitle")}
                      </p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        {t("create.objectiveTileTrafficBody")}
                      </p>
                    </button>
                    <div
                      className={cn(
                        "flex flex-col overflow-hidden rounded-xl border text-left transition",
                        !envMetaPixelId && "opacity-50",
                        envMetaPixelId &&
                          campaignObjective ===
                            META_ADS_CAMPAIGN_OBJECTIVE_SALES
                          ? "border-brand-cyan/50 bg-brand-cyan/10 ring-brand-cyan/20 ring-1"
                          : "border-border bg-muted/20",
                        envMetaPixelId &&
                          campaignObjective !==
                            META_ADS_CAMPAIGN_OBJECTIVE_SALES &&
                          "hover:border-muted-foreground/30",
                      )}
                      title={
                        !envMetaPixelId
                          ? t("create.salesObjectiveDisabledTitle")
                          : undefined
                      }
                    >
                      <button
                        type="button"
                        disabled={!envMetaPixelId}
                        onClick={() => {
                          if (envMetaPixelId) {
                            setCampaignObjective(
                              META_ADS_CAMPAIGN_OBJECTIVE_SALES,
                            );
                          }
                        }}
                        className={cn(
                          "w-full p-4 text-left transition",
                          !envMetaPixelId && "cursor-not-allowed",
                          envMetaPixelId && "hover:bg-white/[0.02]",
                        )}
                        aria-disabled={!envMetaPixelId}
                      >
                        <p className="text-foreground font-semibold">
                          {t("create.objectiveTileSalesTitle")}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">
                          {t("create.objectiveTileSalesBody")}
                        </p>
                      </button>
                      {!envMetaPixelId && (
                        <div className="border-border/50 flex min-w-0 items-start gap-1.5 border-t px-4 pt-2 pb-3">
                          <p className="min-w-0 flex-1 text-xs leading-snug font-medium text-amber-200/90">
                            {t("create.salesObjectiveRequiresPixelBadge")}
                          </p>
                          <ContextHelp
                            className="h-3.5 w-3.5 flex-shrink-0"
                            ariaLabel={t("create.salesPixelHelpAria")}
                            whatIs={t("create.salesPixelHelpWhatIs")}
                            note={t("create.salesPixelNotConfiguredHint")}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <section className={metaAdsFormSectionClass}>
                  <h2 className={metaAdsFormSectionLabelClass}>
                    {t("create.sectionFacebookPage")}
                  </h2>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t("create.pageIdReadOnlyHint")}
                  </p>
                  <div className="mt-4 space-y-2">
                    {envFacebookPageId ? (
                      <div
                        className={cn(
                          "space-y-2 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.07] p-3",
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <p className="min-w-0 flex-1 font-mono text-sm break-all text-white/90">
                            {showEnvPageId
                              ? envFacebookPageId
                              : maskNumericIdForDisplay(envFacebookPageId)}
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowEnvPageId((v) => !v)}
                            className={cn(
                              "text-muted-foreground hover:text-foreground flex shrink-0 items-center justify-center rounded-lg p-1.5 transition",
                              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                            )}
                            aria-pressed={showEnvPageId}
                            aria-label={
                              showEnvPageId
                                ? t("create.pageIdHideToggleAria")
                                : t("create.pageIdShowToggleAria")
                            }
                          >
                            <Icon
                              name={showEnvPageId ? "EyeOff" : "Eye"}
                              className="h-4 w-4"
                              aria-hidden
                            />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                        <div className="flex items-start gap-2">
                          <Icon
                            name="Info"
                            className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-200"
                            aria-hidden
                          />
                          <div className="min-w-0 space-y-1">
                            <p className="text-sm font-medium text-amber-100">
                              {t("create.pageIdNotConfiguredTitle")}
                            </p>
                            <p className="text-xs leading-relaxed text-amber-100/80">
                              {t("create.pageIdNotConfiguredBody")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                <section className={metaAdsFormSectionClass}>
                  <h2 className={metaAdsFormSectionLabelClass}>
                    {t("create.sectionCreative")}
                  </h2>
                  <div className="mt-4 space-y-2">
                    <Label
                      htmlFor="meta-image-url"
                      className={metaAdsFormFieldLabelClass}
                    >
                      {t("create.imageUrlLabel")}
                    </Label>
                    <Input
                      id="meta-image-url"
                      type="url"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder={t("create.imageUrlPlaceholder")}
                      variant="dark"
                      size="lg"
                    />
                    <p className="text-muted-foreground text-xs">
                      {t("create.imageUrlFieldHint")}
                    </p>
                  </div>
                  <div className="mt-4 space-y-2">
                    <Label
                      htmlFor="meta-text"
                      className={metaAdsFormFieldLabelClass}
                    >
                      {t("create.primaryText")}
                    </Label>
                    <textarea
                      id="meta-text"
                      value={primaryText}
                      onChange={(e) => setPrimaryText(e.target.value)}
                      rows={4}
                      placeholder={t("create.primaryTextPlaceholder")}
                      className={metaAdsTextareaClass}
                    />
                    <p className="text-muted-foreground text-xs">
                      {t("create.primaryTextHint")}
                    </p>
                  </div>
                </section>

                <section className={metaAdsFormSectionClass}>
                  <h2 className={metaAdsFormSectionLabelClass}>
                    {t("create.sectionBudget")}
                  </h2>
                  <div className="mt-4 space-y-2">
                    <Label
                      htmlFor="meta-budget"
                      className={metaAdsFormFieldLabelClass}
                    >
                      {t("create.dailyBudgetDollars")}
                    </Label>
                    <div className="relative">
                      <span
                        className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-sm text-gray-400"
                        aria-hidden
                      >
                        $
                      </span>
                      <Input
                        id="meta-budget"
                        inputMode="decimal"
                        value={dailyBudgetDollars}
                        placeholder={t("create.dailyBudgetPlaceholder")}
                        onChange={(e) => setDailyBudgetDollars(e.target.value)}
                        variant="dark"
                        size="lg"
                        className="pl-7"
                      />
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {t("create.dailyBudgetHint")}
                    </p>
                    <div
                      className={cn(
                        "ring-brand-cyan/10 border-border/70 bg-brand-cyan/5 mt-4 rounded-xl border p-4",
                        "ring-1",
                      )}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <Label
                            htmlFor="meta-budget-sharing"
                            className={metaAdsFormFieldLabelClass}
                          >
                            {t("create.budgetSharingLabel")}
                          </Label>
                          <p
                            id="meta-budget-sharing-hint"
                            className="text-muted-foreground text-xs leading-relaxed"
                          >
                            {t("create.budgetSharingHint")}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center sm:pt-0.5">
                          <Switch
                            id="meta-budget-sharing"
                            checked={isAdsetBudgetSharingEnabled}
                            onCheckedChange={setIsAdsetBudgetSharingEnabled}
                            aria-describedby="meta-budget-sharing-hint"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="lg:hidden">
                  <MetaAdsCreativePreview
                    pageName={selectedBusiness?.name}
                    accountLabel={previewAccountLabel}
                    contactEmail={previewContactEmail}
                    primaryText={primaryText}
                    destinationLabel={destinationUrl}
                    imageSrc={previewImageSrc}
                    businessLogo={selectedBusiness?.logo}
                  />
                </div>

                <button
                  type="submit"
                  disabled={disableSubmit}
                  className={cn(
                    "flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-base font-semibold transition disabled:cursor-not-allowed disabled:opacity-50",
                    metaAdsGradientCtaClass,
                  )}
                >
                  {isSaving ? (
                    isEditMode ? (
                      t("create.editSubmitting")
                    ) : (
                      t("create.submitting")
                    )
                  ) : isEditMode ? (
                    <>
                      <Icon name="Save" className="h-5 w-5" aria-hidden />
                      {t("create.editSubmitLabel")}
                    </>
                  ) : (
                    <>
                      <Icon name="Send" className="h-5 w-5" aria-hidden />
                      {t("create.submit")}
                    </>
                  )}
                </button>

                {isEditMode &&
                  Boolean(String(routeCampaignId ?? "").trim()) && (
                    <>
                      <DeleteConfirmationModal
                        confirmLoadingText={t("create.deleteCampaignDeleting")}
                        detailNote={t("create.deleteCampaignIrreversible")}
                        isLoading={deleteCampaignMutation.isPending}
                        isOpen={deleteModalOpen}
                        itemName={name.trim() || String(routeCampaignId)}
                        itemType={t("create.deleteCampaignItemType")}
                        onClose={() => setDeleteModalOpen(false)}
                        onConfirm={handleConfirmDeleteCampaign}
                        subtitle={t("create.deleteCampaignModalSubtitle")}
                        title={t("create.deleteCampaignModalTitle")}
                        confirmButtonText={t("create.deleteCampaignConfirm")}
                      />
                      <div className="border-border/70 mt-14 border-t pt-10">
                        <h2 className="text-foreground mb-5 text-xl font-semibold tracking-tight">
                          {t("create.deleteCampaignSectionLabel")}
                        </h2>
                        <div
                          className={cn(
                            "bg-muted/25 rounded-xl border border-red-500/50",
                            "shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]",
                          )}
                        >
                          <div className="flex flex-col gap-6 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:px-6 sm:py-6">
                            <div className="min-w-0 flex-1 space-y-2">
                              <h3 className="text-foreground text-base leading-snug font-semibold sm:text-[17px]">
                                {t("create.deleteCampaignDangerHeading")}
                              </h3>
                              <p className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
                                {t("create.deleteCampaignSectionHint")}
                              </p>
                            </div>
                            <div className="flex w-full shrink-0 justify-stretch sm:w-auto sm:justify-end sm:pt-0.5">
                              <button
                                type="button"
                                disabled={
                                  !unlocked ||
                                  isCampaignDetailLoading ||
                                  Boolean(isCampaignDetailError) ||
                                  deleteCampaignMutation.isPending ||
                                  isSaving
                                }
                                onClick={() => setDeleteModalOpen(true)}
                                className={metaAdsDangerZoneDeleteButtonClass}
                              >
                                {deleteCampaignMutation.isPending ? (
                                  <Icon
                                    name="Loader2"
                                    className="h-4 w-4 animate-spin"
                                    aria-hidden
                                  />
                                ) : (
                                  <Icon
                                    name="Trash2"
                                    className="h-4 w-4"
                                    aria-hidden
                                  />
                                )}
                                {deleteCampaignMutation.isPending
                                  ? t("create.deleteCampaignDeleting")
                                  : t("create.deleteCampaign")}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
              </div>

              <div className="hidden min-h-0 lg:col-span-1 lg:block">
                <div className="sticky top-6">
                  <MetaAdsCreativePreview
                    pageName={selectedBusiness?.name}
                    accountLabel={previewAccountLabel}
                    contactEmail={previewContactEmail}
                    primaryText={primaryText}
                    destinationLabel={destinationUrl}
                    imageSrc={previewImageSrc}
                    businessLogo={selectedBusiness?.logo}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default MetaAdsCreateCampaignPage;
